import { useEffect, useRef } from 'react';

// Temporary testing-period value: 1 hour instead of CJIS-recommended 30 min.
const IDLE_TIMEOUT_MS = 60 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll'];
export const IDLE_TIMEOUT_LAST_ACTIVITY_KEY = 'idleTimeoutLastActivity';
// Throttle for the persisted-activity localStorage write (see resetTimer).
const ACTIVITY_WRITE_THROTTLE_MS = 1000;

// CJIS-compliant idle session timeout; persists last-activity so idle time survives a tab close/refresh.
export function useIdleTimeout({ onTimeout }) {
  const timerRef = useRef(null);
  const onTimeoutRef = useRef(onTimeout);
  const lastActivityRef = useRef(Date.now());
  const hasTimedOutRef = useRef(false);
  const lastWriteRef = useRef(0);
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    function fireTimeout() {
      if (hasTimedOutRef.current) return;
      hasTimedOutRef.current = true;
      onTimeoutRef.current();
    }

    function armTimer(delayMs) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(fireTimeout, delayMs);
    }

    // localStorage can throw (private browsing, storage disabled/full) —
    // fall back to in-memory-only timing rather than never arming the timer.
    function readPersistedActivity() {
      try {
        return Number(localStorage.getItem(IDLE_TIMEOUT_LAST_ACTIVITY_KEY));
      } catch {
        return 0;
      }
    }

    function writePersistedActivity(value) {
      try {
        localStorage.setItem(IDLE_TIMEOUT_LAST_ACTIVITY_KEY, String(value));
      } catch {
        // storage unavailable — timer still runs in-memory for this session
      }
    }

    function resetTimer() {
      const now = Date.now();
      lastActivityRef.current = now;
      // Throttle the write (not the in-memory reset) — this fires on every
      // mousemove and an unthrottled write would hammer localStorage.
      if (now - lastWriteRef.current >= ACTIVITY_WRITE_THROTTLE_MS) {
        lastWriteRef.current = now;
        writePersistedActivity(now);
      }
      armTimer(IDLE_TIMEOUT_MS);
    }

    // Resume from persisted last activity so idle time survives a
    // tab close/reopen or refresh instead of resetting to zero.
    const persisted = readPersistedActivity();
    if (persisted) {
      lastActivityRef.current = persisted;
      // Clamp: a future persisted timestamp (clock change, or storage edited
      // directly) must not grant more than a fresh IDLE_TIMEOUT_MS window.
      const elapsed = Math.max(0, Date.now() - persisted);
      if (elapsed >= IDLE_TIMEOUT_MS) {
        fireTimeout();
      } else {
        armTimer(IDLE_TIMEOUT_MS - elapsed);
      }
    } else {
      resetTimer();
    }

    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, resetTimer, { passive: true });
    });

    // Guard against browser setTimeout throttling in background tabs:
    // when the tab becomes visible again, check real elapsed time and force
    // logout immediately if the inactivity limit has already passed.
    function handleVisibilityChange() {
      if (document.hidden) return;
      const elapsed = Math.max(0, Date.now() - lastActivityRef.current);
      if (elapsed >= IDLE_TIMEOUT_MS) {
        fireTimeout();
      } else {
        // Re-arm the timer for the remaining time so it fires correctly
        // now that the tab is active and the browser will no longer throttle it.
        armTimer(IDLE_TIMEOUT_MS - elapsed);
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
}
